<template>
  <div class="timeline-section">
    <div class="timeline-header">
      <div>
        <p class="detail-label timeline-label">Timeline</p>
        <p class="timeline-copy">
          Hover markers to preview the ceremony frame, then click a cluster to expand that region.
        </p>
      </div>
      <span class="timeline-duration">{{ formatDuration(timelineDurationSeconds) }}</span>
    </div>

    <div v-if="eventsLoading" class="timeline-loading" aria-hidden="true">
      <div class="timeline-loading-shell">
        <div class="timeline-loading-lane timeline-loading-lane-secondary"></div>
        <div class="timeline-loading-lane timeline-loading-lane-primary"></div>
        <span
          v-for="index in loadingMarkerCount"
          :key="`timeline-loading-marker-${index}`"
          class="timeline-loading-marker"
          :style="loadingMarkerStyle(index)"
        >
          <span class="timeline-loading-marker-core skeleton-block"></span>
          <span class="timeline-loading-marker-label skeleton-block"></span>
        </span>
      </div>
      <div class="timeline-loading-scale">
        <span
          v-for="index in 5"
          :key="`timeline-loading-scale-${index}`"
          class="timeline-loading-scale-tick skeleton-block"
        ></span>
      </div>
    </div>

    <ceremony-timeline-overview
      v-else
      :timeline-duration-seconds="timelineDurationSeconds"
      :clustered-timeline-markers="clusteredTimelineMarkers"
      :active-video-url="activeVideoUrl"
      :format-duration="formatDuration"
      @select-group="$emit('select-group', $event)"
    />

    <transition name="timeline-detail">
      <div
        v-if="!eventsLoading && selectedTimelineGroup && expandedTimelineMarkers.length"
        class="timeline-detail-panel"
        :style="detailMarkerVars"
      >
        <div class="timeline-detail-header">
          <div>
            <p class="timeline-detail-eyebrow">Detailed View</p>
            <h4 class="timeline-detail-title">
              {{ selectedTimelineGroup.events.length }} {{ selectedTimelineGroup.events.length === 1 ? 'result' : 'results' }}
              near {{ formatDuration(selectedTimelineGroup.centerSeconds) }}
            </h4>
          </div>
          <div class="timeline-detail-meta">
            <span class="timeline-detail-chip">
              {{ formatDuration(expandedTimelineRange.startSeconds) }} - {{ formatDuration(expandedTimelineRange.endSeconds) }}
            </span>
          </div>
        </div>

        <div class="timeline-detail-shell">
          <div class="timeline-detail-scale" aria-hidden="true">
            <span
              v-for="tick in detailTicks"
              :key="tick.label"
              class="timeline-detail-tick"
              :style="{ left: `${tick.positionPct}%` }"
            >
              <span class="timeline-detail-tick-line"></span>
              <span class="timeline-detail-tick-label">{{ tick.label }}</span>
            </span>
          </div>

          <div class="timeline-detail-track" :style="{ minHeight: `${expandedTimelineHeightPx / 16}rem` }">
            <div class="timeline-detail-track-fill"></div>
            <button
              v-for="event in expandedTimelineMarkers"
              :key="event.id"
              type="button"
              class="timeline-detail-marker"
              :class="{ 'timeline-detail-marker-active': event.id === selectedEventId }"
              :style="detailMarkerStyle(event)"
              @click="$emit('select-event', event.id)"
            >
              <span class="timeline-detail-marker-dot"></span>
              <span class="timeline-detail-marker-card">
                <span class="timeline-detail-marker-time">{{ formatDuration(event.seconds) }}</span>
                <span class="timeline-detail-marker-title">{{ event.title }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CeremonyTimelineOverview from './CeremonyTimelineOverview.vue'

export default {
  name: 'CeremonyTimeline',
  components: {
    CeremonyTimelineOverview,
  },
  props: {
    eventsLoading: {
      type: Boolean,
      default: false,
    },
    timelineDurationSeconds: {
      type: Number,
      default: 0,
    },
    clusteredTimelineMarkers: {
      type: Array,
      default: () => [],
    },
    selectedTimelineGroup: {
      type: Object,
      default: null,
    },
    expandedTimelineRange: {
      type: Object,
      required: true,
    },
    expandedTimelineMarkers: {
      type: Array,
      default: () => [],
    },
    expandedTimelineHeightPx: {
      type: Number,
      default: 50,
    },
    selectedEventId: {
      type: String,
      default: '',
    },
    activeVideoUrl: {
      type: String,
      default: '',
    },
    formatDuration: {
      type: Function,
      required: true,
    },
  },
  computed: {
    loadingMarkerCount() {
      return 8
    },
    detailMarkerVars() {
      const count = this.expandedTimelineMarkers.length
      if (count <= 2) {
        return {
          '--detail-marker-dot-width': '2.125rem',
          '--detail-marker-dot-height': '1.5rem',
          '--detail-marker-dot-active-width': '2.25rem',
          '--detail-marker-dot-active-height': '1.625rem',
          '--detail-marker-card-min-width': 'clamp(7.5rem, 16vw, 8.25rem)',
          '--detail-marker-card-max-width': 'clamp(11rem, 24vw, 13.125rem)',
        }
      }

      if (count <= 5) {
        return {
          '--detail-marker-dot-width': '1.5rem',
          '--detail-marker-dot-height': '1.125rem',
          '--detail-marker-dot-active-width': '1.625rem',
          '--detail-marker-dot-active-height': '1.25rem',
          '--detail-marker-card-min-width': 'clamp(6.25rem, 13vw, 6.625rem)',
          '--detail-marker-card-max-width': 'clamp(9.5rem, 19vw, 11rem)',
        }
      }

      return {
        '--detail-marker-dot-width': '1.375rem',
        '--detail-marker-dot-height': '1rem',
        '--detail-marker-dot-active-width': '1.5rem',
        '--detail-marker-dot-active-height': '1.125rem',
        '--detail-marker-card-min-width': 'clamp(5.5rem, 11vw, 6rem)',
        '--detail-marker-card-max-width': 'clamp(8.5rem, 17vw, 10.5rem)',
      }
    },
    detailTicks() {
      const startSeconds = Number(this.expandedTimelineRange?.startSeconds || 0)
      const durationSeconds = Math.max(1, Number(this.expandedTimelineRange?.durationSeconds || 1))
      const steps = 4

      return Array.from({ length: steps + 1 }, (_, index) => {
        const ratio = index / steps
        return {
          label: this.formatDuration(startSeconds + (durationSeconds * ratio)),
          positionPct: ratio * 100,
        }
      })
    },
  },
  methods: {
    loadingMarkerStyle(index) {
      const positions = [8, 17, 26, 39, 52, 66, 79, 90]
      const left = positions[index - 1] || 0
      return {
        left: `${left}%`,
      }
    },
    detailMarkerStyle(event) {
      const leftPct = Number.isFinite(event.positionPct) ? event.positionPct : 0
      const topRem = Number.isFinite(event.topPx) ? (event.topPx / 16) : 2.375
      return {
        left: `${leftPct}%`,
        top: `${topRem}rem`,
      }
    },
  },
}
</script>

<style scoped>
.timeline-section {
  margin-top: 1.25rem;
  padding-top: 0.25rem;
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.875rem;
}

.detail-label {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.timeline-label {
  margin-bottom: 0.25rem;
}

.timeline-copy {
  margin: 0.375rem 0 0;
  max-width: 40rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #475569;
}

.timeline-duration {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
}

.timeline-loading {
  margin-top: 0.25rem;
}

.timeline-loading-shell {
  position: relative;
  height: 6.5rem;
  border: 1px solid #dbe4f0;
  border-radius: .25rem;
  background:
    radial-gradient(circle at top left, rgba(226, 232, 240, 0.55), transparent 40%),
    linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(248, 250, 252, 0.98));
  overflow: hidden;
}

.timeline-loading-lane {
  position: absolute;
  left: 1.125rem;
  right: 1.125rem;
  border-radius: 999px;
}

.timeline-loading-lane-primary {
  top: 3.625rem;
  height: 0.875rem;
}

.timeline-loading-lane-secondary {
  top: 2.125rem;
  height: 0.25rem;
}

.timeline-loading-marker {
  position: absolute;
  top: 3.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  transform: translate(-50%, -50%);
}

.timeline-loading-marker-core {
  width: 1.5rem;
  height: 1.375rem;
  border-radius: 0.35rem;
}

.timeline-loading-marker-label {
  width: 2.5rem;
  height: 1.25rem;
  border-radius: .25rem;
}

.timeline-loading-scale {
  display: flex;
  justify-content: space-between;
  margin-top: 0.625rem;
  padding: 0 0.125rem;
}

.timeline-loading-scale-tick {
  width: 2rem;
  height: 0.875rem;
  border-radius: .25rem;
}

.skeleton-block {
  display: block;
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: timeline-skeleton-shimmer 1.3s ease-in-out infinite;
}

.timeline-detail-panel {
  margin-top: 1.125rem;
  border: 1px solid #dbe4f0;
  border-radius: .25rem;
  padding: 1.125rem;
}

.timeline-detail-enter-active,
.timeline-detail-leave-active {
  overflow: hidden;
  transform-origin: top center;
  transition: opacity 0.3s ease, transform 0.3s ease, max-height 0.3s ease, margin-top 0.3s ease, padding 0.3s ease;
}

.timeline-detail-enter,
.timeline-detail-leave-to {
  opacity: 0;
  transform: translateY(-0.75rem) scale(0.98);
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.timeline-detail-enter-to,
.timeline-detail-leave {
  opacity: 1;
  transform: translateY(0) scale(1);
  max-height: 75rem;
}

.timeline-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.timeline-detail-eyebrow {
  margin: 0 0 0.375rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.timeline-detail-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.timeline-detail-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.timeline-detail-chip {
  display: inline-flex;
  align-items: center;
  border-radius: .25rem;
  padding: 0.375rem 0.625rem;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(226, 232, 240, 0.95);
  color: #0f172a;
  font-size: 0.76rem;
  font-weight: 700;
}

.timeline-detail-chip-muted {
  background: rgba(148, 163, 184, 0.14);
  color: #475569;
}

.timeline-detail-shell {
  position: relative;
  margin-top: 1.125rem;
  padding-top: 1.75rem;
}

.timeline-detail-scale {
  position: absolute;
  inset: 0 0.5rem auto;
  height: 1.5rem;
}

.timeline-detail-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
}

.timeline-detail-tick:first-child {
  transform: translateX(0);
}

.timeline-detail-tick:last-child {
  transform: translateX(-100%);
}

.timeline-detail-tick-line {
  display: block;
  width: 1px;
  height: 0.625rem;
  margin: 0 auto 0.25rem;
  background: rgba(100, 116, 139, 0.35);
}

.timeline-detail-tick-label {
  display: block;
  font-size: 0.72rem;
  color: #64748b;
  white-space: nowrap;
}

.timeline-detail-track {
  position: relative;
  overflow: visible;
  padding: 0.625rem 0.625rem 1rem;
}

.timeline-detail-track-fill {
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  top: 1.5rem;
  height: 0.875rem;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.95);
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16);
}

.timeline-detail-marker {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0;
  border: 0;
  background: transparent;
  z-index: 1;
  transform: translateX(-50%);
  transition: transform 0.22s ease, filter 0.22s ease;
}

.timeline-detail-marker:hover {
  z-index: 4;
  transform: translateX(-50%) translateY(-0.25rem);
  filter: saturate(1.05);
}

.timeline-detail-marker-dot {
  flex: none;
  width: var(--detail-marker-dot-width, 22px);
  height: var(--detail-marker-dot-height, 16px);
  border-radius: 0.35rem;
  border: 2px solid #ffffff;
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
  box-shadow: 0 0.375rem 0.75rem rgba(15, 23, 42, 0.1);
  transition: transform 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, width 0.22s ease, height 0.22s ease;
}

.timeline-detail-marker-active .timeline-detail-marker-dot {
  width: var(--detail-marker-dot-active-width, 24px);
  height: var(--detail-marker-dot-active-height, 18px);
  background: #0f172a;
  box-shadow: 0 0 0 0.3125rem rgba(15, 23, 42, 0.08);
}

.timeline-detail-marker-active {
  z-index: 3;
}

.timeline-detail-marker:hover .timeline-detail-marker-dot,
.timeline-detail-marker:focus-visible .timeline-detail-marker-dot {
  transform: scale(1.08);
  box-shadow: 0 0 0 0.375rem rgba(15, 23, 42, 0.08), 0 0.75rem 1.5rem rgba(15, 23, 42, 0.18);
}

.timeline-detail-marker-card {
  display: flex;
  flex-direction: column;
  min-width: var(--detail-marker-card-min-width, 96px);
  max-width: var(--detail-marker-card-max-width, 168px);
  padding: 0.625rem 0.75rem;
  border-radius: .25rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.99);
  box-shadow: 0 0.5rem 1rem rgba(15, 23, 42, 0.05);
  text-align: left;
  transform-origin: left center;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.timeline-detail-marker-active .timeline-detail-marker-card {
  border-color: rgba(15, 23, 42, 0.18);
  background: #ffffff;
  box-shadow: 0 0.75rem 1.5rem rgba(15, 23, 42, 0.08);
}

.timeline-detail-marker:hover .timeline-detail-marker-card,
.timeline-detail-marker:focus-visible .timeline-detail-marker-card {
  transform: scale(1.03);
  box-shadow: 0 0.875rem 1.75rem rgba(15, 23, 42, 0.1);
}

.timeline-detail-marker-time {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.timeline-detail-marker-title {
  margin-top: 0.25rem;
  font-size: 0.84rem;
  line-height: 1.35;
  color: #0f172a;
}

@media (max-width: 767px) {
  .timeline-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .timeline-loading-shell {
    height: 7rem;
  }

  .timeline-detail-panel {
    padding: 0.875rem;
  }

  .timeline-detail-track {
    overflow-x: auto;
  }

  .timeline-detail-marker-card {
    min-width: 5.5rem;
    max-width: 8.25rem;
  }
}

@keyframes timeline-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}
</style>
